import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { AesEncrypt, AesDecrypt } from "../../../../services/crypto";
import WebService from "../../../../services/webservice";
import { useNavigate } from 'react-router-dom';
import { FaVial, FaFileArrowUp, FaShieldHeart, FaCircleInfo } from "react-icons/fa6";
import { ChevronUp, ChevronDown } from 'lucide-react';
import { FaTimes } from "react-icons/fa";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import './OverAll_PeriodTracking.css';
import Swal from 'sweetalert2';
import Loader, { ComponentLoader } from '../../common/Loader';

const OverAllPeriodTracking = ({ onBack }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Custom Pagination State
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('latest');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef(null);
  const isFetching = useRef(false);
  const lastFetchParams = useRef(null);

  const [formData, setFormData] = useState({
    letter: '',
    // language: 'english',
    title: '',
    description: '',
    maritalStatus: '',
    ageCategory: '',
    htmlContent: '',
    imageFile: null,
    imagePreview: '',
  });

  const fetchArticles = async (customLimit = itemsPerPage, page = currentPage) => {
    if (isFetching.current) return;
    
    setLoading(true);
    isFetching.current = true;

    try {
      const payload = {
          limit: customLimit,
          page: page,
          search: debouncedSearch,
          sortBy: sortBy
      };

      const encryptedPayload = AesEncrypt(payload);
      const response = await axios.post(`${WebService}api/get_all_period_articles`, {
        EncData: encryptedPayload,
      });

      if (response.data.response === 'S') {
        const decrypted = AesDecrypt(response.data.data);
        setArticles(decrypted?.data || []);
        setTotalCount(decrypted?.totalCount || 0);
      } else {
        setArticles([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching articles:', err);
      setArticles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

useEffect(() => {
  const params = JSON.stringify({ debouncedSearch, sortBy });
  if (lastFetchParams.current === params) return; // Prevent duplicate call with same params
  lastFetchParams.current = params;

  setArticles([]);        // 🔥 CLEAR OLD DATA
  fetchArticles(itemsPerPage, currentPage);   // fresh fetch
}, [debouncedSearch, sortBy]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',          // red for delete
      cancelButtonColor: '#3085d6',        // blue for cancel
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    // Only proceed if user clicked "Yes"
    if (!result.isConfirmed) return;
    try {
      const encryptedPayload = AesEncrypt({ letter: id });
      const response = await axios.post(`${WebService}api/deleteOverallPeriodArticle`, {
        EncData: encryptedPayload,
      });
      if (response.data.response === 'S') {
        Swal.fire('Deleted!', 'Article deleted successfully.', 'success');
        // console.log("deleted Id : ", AesDecrypt(response.data.data));
        fetchArticles();
      } else {
        Swal.fire('Error', response.data.message || 'Delete failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to delete article', 'error');
    }
  };

  const handleSave = async () => {
    if (!formData.letter || !formData.title || !formData.description || !formData.htmlContent || !formData.maritalStatus || !formData.ageCategory) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }

    // Check for duplicate letter
    const isDuplicate = articles.some(
      (a) =>
        a.letter.trim().toLowerCase() === formData.letter.trim().toLowerCase() &&
        (!editingArticle || a.letter.trim().toLowerCase() !== editingArticle.letter.trim().toLowerCase())
    );

    if (isDuplicate) {
      Swal.fire(`Letter "${formData.letter}" already exists`, `Please enter a new letter.`, 'warning');
      return;
    }

    try {
      const payload = { ...formData }; // Clean and sufficient
      delete payload.imageFile;
      delete payload.imagePreview;

      const encryptedPayload = AesEncrypt(payload);

      const endpoint = editingArticle
        ? `${WebService}api/editOverallPeriodArticle`
        : `${WebService}api/addOverallPeriodArticle`; // ← Fixed!

      const formDataToSend = new FormData();
      formDataToSend.append('EncData', JSON.stringify(encryptedPayload));
      if (formData.imageFile) {
        formDataToSend.append('image', formData.imageFile);
      }

      const response = await axios.post(endpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.response === 'S') {
        Swal.fire('Success', editingArticle ? 'Article updated!' : 'Article added!', 'success');
        setShowModal(false);
        setEditingArticle(null);
        setFormData({
          letter: '',
         // language: 'english',
          title: '',
          description: '',
          maritalStatus: '',
          ageCategory: '',
          htmlContent: '',
          imageFile: null,
          imagePreview: ''
        });
        fetchArticles();
      } else {
        Swal.fire('Error', response.data.message || 'Save failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to save article', 'error');
    }
  };

  const openAddModal = () => {
    setEditingArticle(null);
    setFormData({
      letter: '',
     // language: 'english',
      title: '',
      description: '',
      maritalStatus: '',
      ageCategory: '',
      htmlContent: '',
      imageFile: null,
      imagePreview: ''
    });
    setShowModal(true);
  };

  const openEditModal = (a) => {
    setEditingArticle(a);
    setFormData({
      letter: a.letter || '',
      // language: a.language,
      title: a.title,
      description: a.description,
      maritalStatus: a.maritalStatus,
      ageCategory: a.ageCategory,
      htmlContent: a.htmlContent,
      imageFile: null,
      imagePreview: a.image || ''
    });
    setShowModal(true);
  };

const handlePageChange = (page) => {
  setCurrentPage(page);
  fetchArticles(itemsPerPage, page);
};

const handleItemsPerPageChange = (e) => {
  const newVal = Number(e.target.value);

  // Update limit
  setItemsPerPage(newVal);

  // 🔥 Reset everything 
  setArticles([]);
  setCurrentPage(1);

  // Fetch fresh data with new limit
  fetchArticles(newVal, 1);
};

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(val);
        setCurrentPage(1);
        setArticles([]);     // 🔥 IMPORTANT
    }, 500);  
  };

  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const handleSort = (field) => {
      let direction = 'asc';
      if (sortConfig.key === field && sortConfig.direction === 'asc') {
          direction = 'desc';
      }
      setSortConfig({ key: field, direction });
  };

  const sortedData = useMemo(() => {
      if (!articles) return [];
      let sortableItems = [...articles];
      if (sortConfig.key) {
          sortableItems.sort((a, b) => {
              let aValue = a[sortConfig.key];
              let bValue = b[sortConfig.key];

              aValue = aValue ? aValue.toString().toLowerCase() : '';
              bValue = bValue ? bValue.toString().toLowerCase() : '';

              if (aValue < bValue) {
                  return sortConfig.direction === 'asc' ? -1 : 1;
              }
              if (aValue > bValue) {
                  return sortConfig.direction === 'asc' ? 1 : -1;
              }
              return 0;
          });
      }
      return sortableItems;
  }, [articles, sortConfig]);

  const renderSortHeader = (label, field) => (
      <th style={{ whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{label}</span>
              <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                  <ChevronUp size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'asc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                  <ChevronDown size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'desc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
              </span>
          </div>
      </th>
  );

  if (loading && articles.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px', width: '100%' }}>
        <ComponentLoader color="#4f46e5" size="50px" />
      </div>
    );
  }

  return (
    <div className="menstrual-questions-container">
      <div className="header-bar">
        <h2>Period Tracking Articles</h2>
      </div>
      <div className="ptr-header-bar" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="search-wrapper" style={{ margin: 0 }}>
              <input
                type="text"
                placeholder="Search by Letter or Title..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              {searchTerm && (
                <button className="clear-btn" onClick={() => handleSearchChange({ target: { value: '' } })}>
                  <FaTimes />
                </button>
              )}
            </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Sort by:</span>
            <select 
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
                setArticles([]);     // 🔥 IMPORTANT
              }}
              style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500', backgroundColor: '#ffffff', color: '#1e293b', cursor: 'pointer', outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
            >
              <option value="latest">Latest data</option>
              <option value="oldest">Oldest data</option>
              <option value="az">Letter: A - Z</option>
              <option value="za">Letter: Z - A</option>
            </select>
          </div>
          <button className="add-btn" onClick={openAddModal}>+</button>
          <button className="total-count"><span style={{fontSize: '12px'}}>Total Articles : {totalCount || 0}</span></button>
        </div>
      </div>
      
      <div className="ptr-custom-table-wrapper">
        <table className="ptr-custom-table">
          <thead>
            <tr>
              {renderSortHeader('Letter', 'letter')}
              {renderSortHeader('Title', 'title')}
              {renderSortHeader('Description', 'description')}
              {/* <th>Language</th> */}
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" style={{ padding: '80px 0' }}>
                <ComponentLoader color="#4f46e5" size="40px" />
              </td>
            </tr>
          ) : articles.length === 0 ? (
            <tr>
              <td colSpan="5" className="empty">
                {searchTerm
                  ? "No matching articles found"
                  : "No articles found. Click + to add."}
              </td>
            </tr>
          ) : (
            sortedData.map((a) => (
              <tr key={a.letter}>
                <td>{a.letter}</td>
                <td>
                  {a.title.length > 100
                    ? `${a.title.substring(0, 100)}...`
                    : a.title}
                </td>
                <td>
                  {a.description.length > 100
                    ? `${a.description.substring(0, 100)}...`
                    : a.description}
                </td>
                {/* <td>{a.language}</td> */}
                <td>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="edit-btn" onClick={() => openEditModal(a)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(a.letter)}>
                      Delete
                    </button>
                    <button
                      className="info-btn"
                      onClick={() => {
                        setSelectedArticle(a);
                        setShowDetailsModal(true);
                      }}
                      title="View Details"
                    >
                      <FaCircleInfo />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ padding: '15px 20px', background: '#fff', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
          <div className="d-flex align-items-center">
              <span style={{ fontSize: '13px', color: '#64748b', marginRight: '10px' }}>
                  Showing {totalCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} – {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries. Per page:
              </span>
              <select 
                  value={itemsPerPage} 
                  onChange={handleItemsPerPageChange}
                  style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#ffffff', color: '#1e293b', cursor: 'pointer', outline: 'none' }}
              >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
              </select>
          </div>
          
          <Pagination size="sm" className="mb-0">
              <PaginationItem className="pq-page-item" disabled={currentPage === 1}>
                  <PaginationLink className="pq-page-link" previous onClick={() => handlePageChange(currentPage - 1)} />
              </PaginationItem>

              {[...Array(Math.ceil(totalCount / itemsPerPage))].map((_, i) => {
                  const totalPages = Math.ceil(totalCount / itemsPerPage);
                  if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                      return (
                          <PaginationItem className="pq-page-item" active={i + 1 === currentPage} key={i}>
                              <PaginationLink className="pq-page-link" onClick={() => handlePageChange(i + 1)}>
                                  {i + 1}
                              </PaginationLink>
                          </PaginationItem>
                      );
                  }
                  if (i === 1 && currentPage > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                  if (i === totalPages - 2 && currentPage < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                  return null;
              })}

              <PaginationItem className="pq-page-item" disabled={currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0}>
                  <PaginationLink className="pq-page-link" next onClick={() => handlePageChange(currentPage + 1)} />
              </PaginationItem>
          </Pagination>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingArticle ? 'Edit' : 'Add'} Article</h3>
            <div className="form-group">
              <label>Letter <span className="required">*</span></label>
              <input
                value={formData.letter}
                onChange={(e) => setFormData({ ...formData, letter: e.target.value })}
                placeholder="e.g., A1, C1"
                disabled={!!editingArticle}
                className={editingArticle ? 'disabled-input' : ''}
              />
            </div>

            {/* future purpose to add multiple languages */}
            {/* <div className="form-group">
              <label>Language</label>
              <input
                value="English"
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                disabled={!!editingArticle}
                className={editingArticle ? 'disabled-input' : ''}
                readOnly
              />
            </div> */}

            <div className="form-group">
              <label>Title</label>
              <input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Upload Image</label>
              <input
                type="file"
                accept=".png, .jpg, .jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData({
                      ...formData,
                      imageFile: file,
                      imagePreview: URL.createObjectURL(file)
                    });
                  }
                }}
              />
              {formData.imagePreview && (
                <div style={{ marginTop: '10px' }}>
                  <img src={formData.imagePreview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Marital Status</label>
              <select
                value={formData.maritalStatus}
                onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}
              >
                <option value="">Select Marital Status</option>
                <option value="All">All</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
              </select>
            </div>
            <div className="form-group">
              <label>Age Group</label>
              <select
                value={formData.ageCategory}
                onChange={(e) => setFormData({ ...formData, ageCategory: e.target.value })}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}
              >
                <option value="">Select Age Group</option>
                <option value="All">All Ages</option>
                <option value="13-18">13 - 18</option>
                <option value="18-40">18 - 40</option>
                <option value="40+">40+</option>
              </select>
            </div>
            <div className="form-group">
              <label>HTML Content</label>
              <textarea
                value={formData.htmlContent}
                onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                rows={10}
                className="html-textarea"
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleSave}>
                {editingArticle ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedArticle && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Article Details</h3>
            <div className="details-section">
              <div className="detail-row">
                <strong>Letter:</strong> <span>{selectedArticle.letter || '-'}</span>
              </div>
              {/* <div className="detail-row">
                <strong>Language:</strong> <span>{selectedArticle.language}</span>
              </div> */}
              <div className="detail-row">
                <strong>Title:</strong> <span>{selectedArticle.title}</span>
              </div>
              <div className="detail-row">
                <strong>Description:</strong> <span>{selectedArticle.description}</span>
              </div>
              <div className="detail-row">
                <strong>Marital Status:</strong> <span>{selectedArticle.maritalStatus}</span>
              </div>
              <div className="detail-row">
                <strong>Age Group:</strong> <span>{selectedArticle.ageCategory}</span>
              </div>
              {selectedArticle.image && (
                <div className="detail-row">
                  <strong>Image:</strong>
                  <div style={{ marginTop: '10px' }}>
                    <img src={selectedArticle.image} alt="Article" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                  </div>
                </div>
              )}
              <div className="detail-row html-preview">
                <strong>HTML Content Preview:</strong>
                <div className="html-content-preview" dangerouslySetInnerHTML={{ __html: selectedArticle.htmlContent }} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default OverAllPeriodTracking;